/**
 * Pure helpers for the 6-digit email codes (§2 "Email codes"): generation, hashing,
 * address checks and the rate-limit window. Web Crypto only, so they run in the Convex
 * runtime, in the Node action and under `bun test`.
 */
import { randomHex, sha256Hex } from './crypto';

export const CODE_LENGTH = 6;
export const CODE_TTL_MS = 10 * 60 * 1000;
export const MAX_ATTEMPTS = 5;
export const RATE_WINDOW_MS = 15 * 60 * 1000;
export const RATE_MAX_IN_WINDOW = 3;
export const RATE_MIN_GAP_MS = 60 * 1000;
/** Codes (and dev outbox rows) are deleted this long after they expire. */
export const CODE_RETENTION_MS = 24 * 60 * 60 * 1000;
export const EMAIL_MAX_LENGTH = 254;

const CODE_SPACE = 10 ** CODE_LENGTH;
// Largest multiple of CODE_SPACE below 2^32: values at or above it are redrawn, so every code is equally likely.
const UNBIASED_LIMIT = Math.floor(0x1_0000_0000 / CODE_SPACE) * CODE_SPACE;

/** Six decimal digits, uniform over 000000–999999 (rejection sampling on Web Crypto). */
export function randomLoginCode(): string {
  const buf = new Uint32Array(1);
  for (;;) {
    crypto.getRandomValues(buf);
    const n = buf[0]!;
    if (n < UNBIASED_LIMIT) return String(n % CODE_SPACE).padStart(CODE_LENGTH, '0');
  }
}

/** "123456" → "123 456", the way emails and the UI show a code. */
export function formatLoginCode(code: string): string {
  return `${code.slice(0, 3)} ${code.slice(3)}`;
}

/** What a person typed ("123 456", " 123-456 ") → "123456", or null when it is not six digits. */
export function normalizeLoginCode(input: string): string | null {
  const digits = input.replace(/[\s-]/g, '');
  return /^\d{6}$/.test(digits) ? digits : null;
}

export async function hashLoginCode(code: string, salt: string): Promise<string> {
  return sha256Hex(`${salt}:${code}`);
}

export async function newLoginCodeHash(code: string): Promise<{ codeHash: string; codeSalt: string }> {
  const codeSalt = randomHex(16);
  return { codeHash: await hashLoginCode(code, codeSalt), codeSalt };
}

/** Trimmed and lower-cased. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

// One "@", a non-empty local part without spaces, a dotted domain of letters, digits and hyphens.
const EMAIL_RE = /^[^\s@"(),:;<>[\\\]]+@(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** A simple RFC-ish check on a normalised address: shape, at most 254 characters, local part at most 64. */
export function isValidEmail(email: string): boolean {
  if (email.length === 0 || email.length > EMAIL_MAX_LENGTH) return false;
  if (!EMAIL_RE.test(email)) return false;
  const local = email.slice(0, email.lastIndexOf('@'));
  return local.length <= 64 && !local.startsWith('.') && !local.endsWith('.') && !local.includes('..');
}

/**
 * Whether one more code may be sent, given when earlier codes for the same address and purpose
 * were created: at most `RATE_MAX_IN_WINDOW` per `RATE_WINDOW_MS` and one per `RATE_MIN_GAP_MS`.
 * `retryInMs` is how long until the next one would be allowed.
 */
export function rateLimit(createdAts: readonly number[], now: number): { ok: true } | { ok: false; retryInMs: number } {
  const recent = createdAts.filter((t) => t > now - RATE_WINDOW_MS && t <= now).sort((a, b) => b - a);
  let retryInMs = 0;
  const newest = recent[0];
  if (newest !== undefined && now - newest < RATE_MIN_GAP_MS) retryInMs = newest + RATE_MIN_GAP_MS - now;
  if (recent.length >= RATE_MAX_IN_WINDOW) {
    const oldestCounted = recent[RATE_MAX_IN_WINDOW - 1]!;
    retryInMs = Math.max(retryInMs, oldestCounted + RATE_WINDOW_MS - now);
  }
  return retryInMs > 0 ? { ok: false, retryInMs } : { ok: true };
}
